import React, { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';

export interface ClusterPopupDevice {
  deviceNumber: string;
  name: string | null;
  color: string;
  icon: string;
  hasWarning: boolean;
}

interface DeviceClusterPopupProps {
  devices: ClusterPopupDevice[];
  selectedDeviceNumber?: string | null;
  onDeviceClick: (deviceNumber: string) => void;
}

export const DeviceClusterPopup: FC<DeviceClusterPopupProps> = ({
  devices,
  selectedDeviceNumber,
  onDeviceClick,
}: DeviceClusterPopupProps) => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [isAtTop, setIsAtTop] = useState<boolean>(true);
  const [isAtBottom, setIsAtBottom] = useState<boolean>(true);

  useEffect(() => {
    const scrollContainer = containerRef.current;
    if (!scrollContainer) return;

    const updateScrollEdges = (): void => {
      setIsAtTop(scrollContainer.scrollTop <= 0);
      setIsAtBottom(
        scrollContainer.scrollTop + scrollContainer.clientHeight >= scrollContainer.scrollHeight - 1
      );
    };
    updateScrollEdges();
    scrollContainer.addEventListener('scroll', updateScrollEdges);
    return (): void => {
      scrollContainer.removeEventListener('scroll', updateScrollEdges);
    };
  }, [devices]);

  return (
    <div>
      <div className="px-1 pb-2 text-sm font-semibold text-gray-800">
        {t('map_cluster_devices_header', { deviceCount: devices.length })}
      </div>
      <div ref={containerRef} className="max-h-52 overflow-y-auto overscroll-contain">
        <div
          className={clsx(
            'pointer-events-none sticky top-0 z-10 -mb-8 h-8 bg-gradient-to-b from-white to-transparent transition-opacity duration-150',
            isAtTop ? 'opacity-0' : 'opacity-100'
          )}
        />
        <div className="space-y-0.5">
          {devices.map((device: ClusterPopupDevice) => {
            const isSelected = selectedDeviceNumber === device.deviceNumber;
            return (
              <button
                key={device.deviceNumber}
                type="button"
                onClick={(): void => onDeviceClick(device.deviceNumber)}
                className={clsx(
                  'm-0 flex w-full cursor-pointer appearance-none items-center gap-2.5 rounded-lg border-0 px-1.5 py-1.5 text-left font-sans outline-none transition-colors',
                  isSelected ? 'bg-blue-50' : 'bg-transparent hover:bg-gray-100'
                )}
              >
                <span
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full ring-1 ring-black/10"
                  style={{ backgroundColor: device.color }}
                >
                  <img src={device.icon} alt="" className="h-6 w-6" />
                </span>
                <span
                  className={clsx(
                    'min-w-0 flex-1 break-words text-sm leading-snug text-gray-700',
                    isSelected ? 'font-bold' : 'font-normal'
                  )}
                >
                  {device.name || device.deviceNumber}
                </span>
                {device.hasWarning && (
                  <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-red-600 text-[10px] font-bold leading-none text-white">
                    !
                  </span>
                )}
              </button>
            );
          })}
        </div>
        <div
          className={clsx(
            'pointer-events-none sticky bottom-0 z-10 -mt-8 h-8 bg-gradient-to-t from-white to-transparent transition-opacity duration-150',
            isAtBottom ? 'opacity-0' : 'opacity-100'
          )}
        />
      </div>
    </div>
  );
};
